import { CalendarDays, CalendarCheck } from "lucide-react";
import { SessionCard } from "./SessionCard";

function DayHeaderIcon({ overlap, size }) {
  return overlap ? <CalendarCheck size={size} className="text-white" /> : <CalendarDays size={size} className="text-[#38BDF8]" />;
}

const AllHereBadge = () => <span className="text-[10px] font-bold bg-white/25 rounded-full px-2 py-0.5">All here</span>;

// Desktop grid column for one day.
export function DayColumn({ day, sessions, overlap, bookingUrl }) {
  return (
    <div className={`flex flex-col rounded-2xl transition-colors ${overlap ? "bg-[#10B981]/8 ring-1 ring-[#10B981]/30 p-2 -m-0" : ""}`}>
      <div className={`flex items-center gap-2 justify-center rounded-2xl text-white py-3 mb-3 ${overlap ? "bg-[#10B981]" : "bg-[#0A2540]"}`}>
        <DayHeaderIcon overlap={overlap} size={15} />
        <span className="font-heading font-semibold text-sm">{day.slice(0, 3)}</span>
        {overlap && <AllHereBadge />}
      </div>
      <div className="flex flex-col gap-2.5">
        {sessions.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">No classes</div>
        )}
        {sessions.map((s, i) => (
          <SessionCard key={`${day}-${s.cls}-${s.time}-${i}`} s={s} index={i} bookingUrl={bookingUrl} />
        ))}
      </div>
    </div>
  );
}

// Mobile / tablet stacked card for one day.
export function DayListCard({ day, sessions, overlap, bookingUrl }) {
  return (
    <div className={`rounded-2xl border overflow-hidden ${overlap ? "border-[#10B981]/40 bg-[#10B981]/[0.06]" : "border-slate-100 bg-white"}`}>
      <div className={`flex items-center gap-2 px-4 py-3 text-white ${overlap ? "bg-[#10B981]" : "bg-[#0A2540]"}`}>
        <DayHeaderIcon overlap={overlap} size={16} />
        <span className="font-heading font-semibold text-sm">{day}</span>
        <span className="ml-auto text-[11px] font-semibold text-white/70">
          {sessions.length} class{sessions.length > 1 ? "es" : ""}
        </span>
        {overlap && <AllHereBadge />}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 p-3">
        {sessions.map((s, i) => (
          <SessionCard key={`m-${day}-${s.cls}-${s.time}-${i}`} s={s} index={i} bookingUrl={bookingUrl} />
        ))}
      </div>
    </div>
  );
}

export function OverlapSummary({ selectedCount, overlapDays }) {
  const has = overlapDays.length > 0;
  return (
    <div
      className={`mb-8 flex items-start gap-3 rounded-2xl px-5 py-4 border ${has ? "bg-[#10B981]/8 border-[#10B981]/30" : "bg-slate-50 border-slate-200"}`}
      data-testid="overlap-summary"
    >
      <span className={`grid place-items-center w-8 h-8 rounded-full shrink-0 ${has ? "bg-[#10B981] text-white" : "bg-slate-200 text-slate-500"}`}>
        <CalendarCheck size={16} />
      </span>
      <p className="text-sm text-[#334155] pt-1">
        {has ? (
          <>
            <span className="font-semibold text-[#0F172A]">All {selectedCount} classes run back-to-back</span> on{" "}
            <span className="font-semibold text-[#0E9F6E]">{overlapDays.join(", ")}</span> — one trip covers everyone, great for siblings.
          </>
        ) : (
          <>Your selected classes don't run close together on the same day, so they'd need separate visits.</>
        )}
      </p>
    </div>
  );
}

export const LEGEND = [
  ["bg-[#10B981]", "Spaces available"],
  ["bg-[#F59E0B]", "Only a few left"],
  ["bg-[#EF4444]", "Full / waiting list"],
  ["bg-[#0A2540]", "Contact us (school-age)"],
];

export function liveStatus(isLoading, isLive) {
  if (isLoading) return { label: "Loading live availability…", dot: "bg-amber-400 animate-pulse" };
  if (isLive) return { label: "Live availability", dot: "bg-[#10B981] animate-pulse" };
  return { label: "Showing typical schedule", dot: "bg-slate-300" };
}
